import { config } from '../config';
import type { EnvironmentReport, RunCondition, RunMode, RunRecord, TrialSample } from '../types';
import { countOver, fractionOver, histogram, summarize } from './stats';
import type { HistogramBucket, Summary } from './stats';

/**
 * Pure verdict logic for build unit U-0.
 *
 * This module decides what a set of measurements *means* — whether assumption A-1 holds, whether
 * NFR-1's budget is met, and whether the run is admissible as evidence at all. It is separated
 * from the runner so the classification can be unit-tested at exact threshold boundaries without
 * a device, a database or a clock.
 *
 * It deliberately does not soften anything. `verify-observable-behavior-not-proxy`: a green test
 * suite, a fast desktop number and "it ran" are all proxies. The only thing that discharges A-1 is
 * a measurement taken on the target device, and this module says so in the output rather than
 * leaving the reader to remember it.
 */

export type Verdict = 'pass' | 'fail' | 'inconclusive';

export interface ThresholdCheck {
  /** Requirement or assumption id this check answers. */
  id: string;
  label: string;
  /** The statistic compared. */
  statistic: string;
  observedMs: number;
  budgetMs: number;
  verdict: Verdict;
  detail: string;
}

/**
 * Why a run may not be used as acceptance evidence.
 *
 * ENV-1 is a MUST: "Desktop Chrome supported for development only, and is never acceptance
 * evidence for a runtime or behavioral requirement." A run that fails this gate still produces
 * numbers — they are useful for calibration — but they do not answer A-1 and the report says so
 * in those words.
 */
export interface EvidenceGate {
  admissible: boolean;
  blockers: string[];
  warnings: string[];
}

export interface ConditionReport {
  condition: RunCondition;
  runId: string;
  /**
   * How the taps were produced. Load-bearing, not descriptive: `auto` dispatches synthetically and
   * so measures from *inside* JavaScript, while `liveTap` anchors on a real `pointerup`. The two
   * `total` figures are not comparable and must never be averaged or ranked together.
   */
  mode: RunMode;
  sampleCount: number;
  failureCount: number;
  /** `dispatch → IDBTransaction 'complete'` — the A-1 statistic. */
  commit: Summary;
  /** `anchor → next frame` — the NFR-1 statistic. */
  total: Summary;
  /**
   * `pointerup → dispatch` — the touch-delivery leg. Real only in `liveTap` mode; identically zero
   * under `auto`, which is precisely why an auto run understates NFR-1.
   */
  input: Summary;
  /**
   * Peak foreign heat workers during sampling; `null` when the run predates the check.
   * `0` = checked and clean. `null` = not checked. They are not the same and must not render alike.
   */
  foreignHeatWorkersDuringRun: number | null;
  /** `known` only when the run positively attests a clean sampling window. */
  provenance: 'known' | 'unknown';
  commitHistogram: HistogramBucket[];
  overCommitBudget: { count: number; fraction: number };
  overNfr1Budget: { count: number; fraction: number };
  /** Slowest single commit observed. The number that decides whether a match can be trusted. */
  worstCommitMs: number;
  checks: ThresholdCheck[];
  gate: EvidenceGate;
  /** True when the sample is too small for the p99 to mean anything. */
  p99Underpowered: boolean;
}

/** The design finding this unit exists to resolve. */
export interface F9Determination {
  /** `holds` — A-1 is true and SES-2 + LIVE-2 do not collide. */
  outcome: 'holds' | 'breached' | 'inconclusive';
  headline: string;
  detail: string;
}

/**
 * Which measurements the report's NFR-1 statement actually rests on.
 *
 * The first real-device session showed why this cannot be left implicit. Live taps recorded an
 * `inputMs` of 17–28 ms — the cost of touch delivery before any of our code runs — while every
 * automatic run reports `inputMs: 0` because it dispatches synthetically. Total p95 was **41 ms
 * real against 17 ms synthetic**. Both sit inside the 100 ms budget, so nothing is broken, but the
 * automatic runs understate the real path by more than half, and design §5.3's headroom reasoning
 * was calibrated against the understated figure. Two numbers that look comparable and are not is
 * exactly how that reasoning goes wrong a second time.
 */
export interface Nfr1Basis {
  /** `liveTap` when a real-touch run is available; `synthetic` when only auto runs are; `none` when neither is admissible. */
  basis: 'liveTap' | 'synthetic' | 'none';
  /** p50 of the real touch-delivery leg, from admissible live-tap runs. */
  realInputP50Ms: number | null;
  /** p95 of tap→ack from admissible live-tap runs — the honest NFR-1 figure. */
  realTotalP95Ms: number | null;
  /** p95 of tap→ack from admissible auto runs — the understated figure. */
  syntheticTotalP95Ms: number | null;
  /** How much the synthetic path understates the real one, in milliseconds. Never negative. */
  understatementMs: number | null;
  /**
   * True when the comparison produced a negative understatement.
   *
   * Real taps include touch delivery and synthetic ones do not, so the real path can only be
   * slower. A negative value is therefore arithmetically impossible for a valid comparison and
   * means the comparator drew on a run it should not have — it is reported as a bug signal rather
   * than printed as a figure. It reached the screen once as "understate the real path by -54 ms",
   * because a contaminated 91 ms run was serving as the synthetic comparator.
   */
  anomalous: boolean;
  note: string;
}

/**
 * The answer to the question this whole build unit exists to ask, stated plainly.
 *
 * A reader used to see "F-9 FIRES" first and had to reconstruct the truth from per-run tables —
 * which is exactly the failure mode this instrument exists to prevent. A-1's status is now the
 * first thing on the screen, and it names the runs it rests on so it can be checked.
 */
export interface A1Determination {
  outcome: 'passing' | 'failing' | 'unsettled';
  headline: string;
  detail: string;
  /** The admissible, known-clean runs the verdict rests on. */
  runIds: string[];
}

export interface HarnessReport {
  generatedAt: number;
  conditions: ConditionReport[];
  gate: EvidenceGate;
  a1: A1Determination;
  nfr1Basis: Nfr1Basis;
  f9: F9Determination;
  /** Stored runs excluded from every aggregate, and why — so an ignored run is never a silent one. */
  excluded: { runId: string; condition: RunCondition; reason: string }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Evidence admissibility (ENV-1)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * **The single ENV-1 decision in the codebase.** Every harness composes this; none re-implements it.
 *
 * ENV-1 is a MUST — desktop Chrome "is never acceptance evidence for a runtime or behavioral
 * requirement" — and it is a requirement of the form *must be prevented*. A second copy of this
 * judgement is how one of them ends up softer than the other, so the platform half lives here and
 * each unit adds only its own **evidence-sufficiency** blockers on top (sample floors, segment
 * counts) via `mergeGates`.
 *
 * Note that "installed home-screen PWA" is a blocker, not a warning. A Safari tab and an installed
 * PWA are different browsing contexts with different storage grants (`navigator.storage.persist()`
 * behaves differently, A-4) — and the requirement names the installed PWA specifically.
 *
 * BUILD-UNIT NOTE: extracted by U-1 from `evaluateGate`, unchanged in behavior. U-1 measures audio
 * segments, where U-0's 50-trial floor is meaningless, so the gate had to be composable rather than
 * bypassable.
 */
export function evaluatePlatformGate(env: EnvironmentReport): EvidenceGate {
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (!env.looksLikeIosSafari) {
    blockers.push(
      'ENV-1: this run was not on iOS Safari. Desktop or non-Safari numbers are development calibration only and do not discharge the assumption under test.',
    );
  }
  if (!env.standalone) {
    blockers.push(
      'ENV-1: this run was not in the installed home-screen PWA. Run it from the home-screen icon, not a Safari tab.',
    );
  }
  if (env.timerResolutionMs > 1) {
    warnings.push(
      `performance.now() resolves to ${env.timerResolutionMs} ms on this device. Every figure below is quantised to that step; differences smaller than it are not real.`,
    );
  }

  return { admissible: blockers.length === 0, blockers, warnings };
}

/** Combines gates so a run is admissible only if **every** contributing gate says so. */
export function mergeGates(...gates: readonly EvidenceGate[]): EvidenceGate {
  const blockers = [...new Set(gates.flatMap((g) => g.blockers))];
  const warnings = [...new Set(gates.flatMap((g) => g.warnings))];
  return { admissible: blockers.length === 0, blockers, warnings };
}

/**
 * ENV-1 gate for a U-0 latency run: the platform gate plus U-0's own sample-size sufficiency.
 *
 * A run with fifty fast trials on the right device is still not evidence about a tail, so the
 * sample floor is a blocker rather than a warning.
 */
export function evaluateGate(
  env: EnvironmentReport,
  samples: readonly TrialSample[],
  /**
   * `null` = provenance unknown. Deliberately has **no default**: a default of `0` would mean
   * "assume clean", which is the assumption that let a contaminated run keep voting.
   */
  foreignHeatWorkersDuringRun: number | null,
): EvidenceGate {
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (foreignHeatWorkersDuringRun === null) {
    // Its own state, distinct from contamination. "We did not check" must never read the same as
    // "we checked and it was fine" — see `RunRecord.foreignHeatWorkersDuringRun`.
    blockers.push(
      'Provenance unknown: this run was recorded before the harness checked whether another run’s ' +
        'heat load overlapped its sampling window, so it cannot attest that it was measured clean. ' +
        'It is kept — deleting evidence is worse than mis-scoring it — but it is excluded from the ' +
        'aggregate verdicts rather than allowed to vote on them.',
    );
  }

  if (foreignHeatWorkersDuringRun !== null && foreignHeatWorkersDuringRun > 0) {
    // The same class of blocker as too few samples, and for the same reason: the numbers are real,
    // but they are not a measurement of the thing under test. A run whose sampling window overlapped
    // another run's heat load once published a false `breached` finding — see `runLock.ts`.
    blockers.push(
      `${foreignHeatWorkersDuringRun} heat worker(s) belonging to another run were live during this run's sampling window. ` +
        'It measured under load it did not ask for, so its figures describe a device state no user will ever be in.',
    );
  }
  if (samples.length < config.HARNESS_TRIALS_MIN) {
    blockers.push(
      `Sample size ${samples.length} is below the ${config.HARNESS_TRIALS_MIN}-trial floor. A tail figure from this few trials is not a tail figure.`,
    );
  }
  if (samples.length < config.HARNESS_P99_MIN_SAMPLES) {
    warnings.push(
      `Fewer than ${config.HARNESS_P99_MIN_SAMPLES} trials: the p99 is reported but rests on fewer than 2 observations above it.`,
    );
  }

  return mergeGates(evaluatePlatformGate(env), {
    admissible: blockers.length === 0,
    blockers,
    warnings,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Threshold checks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * **The gate-first rule, in one expression.** Admissibility is consulted *before* the observation
 * is looked at, so no threshold comparison can produce `pass` on an inadmissible run however good
 * the number was. Every check in every harness routes through this or through `checkVerdict` below,
 * which is written in terms of it.
 */
export function gatedVerdict(ok: boolean, admissible: boolean): Verdict {
  if (!admissible) return 'inconclusive';
  return ok ? 'pass' : 'fail';
}

function checkVerdict(observed: number, budget: number, admissible: boolean): Verdict {
  return gatedVerdict(observed <= budget, admissible);
}

function buildChecks(
  condition: RunCondition,
  commit: Summary,
  total: Summary,
  admissible: boolean,
): ThresholdCheck[] {
  const checks: ThresholdCheck[] = [
    {
      id: 'NFR-1',
      label: 'Tap to acknowledgment begins',
      statistic: 'p95 of anchor → first paintable frame',
      observedMs: total.p95,
      budgetMs: config.TAP_ACK_BUDGET_MS,
      verdict: checkVerdict(total.p95, config.TAP_ACK_BUDGET_MS, admissible),
      detail:
        'NFR-1 covers the whole path: dispatch, the two-row commit, and the frame boundary before the LIVE-2 flash can paint.',
    },
    {
      id: 'NFR-1 (tail)',
      label: 'Worst-case tap to acknowledgment',
      statistic: 'p99 of anchor → first paintable frame',
      observedMs: total.p99,
      budgetMs: config.TAP_ACK_BUDGET_MS,
      verdict: checkVerdict(total.p99, config.TAP_ACK_BUDGET_MS, admissible),
      detail:
        'A p50 that passes while the p99 fails is the case that corrupts a live match: the user stops checking the screen, and the late tap is the one they never notice.',
    },
    {
      id: 'design §5.3',
      label: 'Commit inside ACK_COMMIT_BUDGET_MS',
      statistic: 'p95 of dispatch → IDB complete',
      observedMs: commit.p95,
      budgetMs: config.ACK_COMMIT_BUDGET_MS,
      verdict: checkVerdict(commit.p95, config.ACK_COMMIT_BUDGET_MS, admissible),
      detail:
        'A logged measurement threshold, never a UI branch. Exceeding it is not an error; it is the number written into the match archive.',
    },
  ];

  // A-1 is stated for the *warm* case specifically. Asserting it against a cold or thermally
  // loaded run would be checking a claim the design never made.
  if (condition === 'warm') {
    checks.unshift({
      id: 'A-1',
      label: 'Two-row readwrite commits within the assumed warm p95',
      statistic: 'p95 of dispatch → IDB complete',
      observedMs: commit.p95,
      budgetMs: config.A1_ASSUMED_WARM_P95_MS,
      verdict: checkVerdict(commit.p95, config.A1_ASSUMED_WARM_P95_MS, admissible),
      detail:
        'The single highest-risk unvalidated assumption in the design (spec §0.1). U-0 exists to settle it.',
    });
  }

  return checks;
}

// ─────────────────────────────────────────────────────────────────────────────
// Report construction
// ─────────────────────────────────────────────────────────────────────────────

/** Histogram edges: the two budgets that matter, plus a coarse spread either side of them. */
export function commitHistogramEdges(): number[] {
  return [
    5,
    10,
    20,
    config.A1_ASSUMED_WARM_P95_MS,
    config.ACK_COMMIT_BUDGET_MS,
    config.TAP_ACK_BUDGET_MS,
    250,
  ];
}

/**
 * @throws {import('./stats').StatsInputError} when the run carries no usable samples. A run that
 *   produced nothing must not render as a passing report; the caller surfaces the error.
 */
export function reportForRun(run: RunRecord): ConditionReport {
  const commitValues = run.samples.map((s) => s.commitMs);
  const totalValues = run.samples.map((s) => s.totalMs);
  const inputValues = run.samples.map((s) => s.inputMs);

  const commit = summarize(commitValues);
  const total = summarize(totalValues);
  const input = summarize(inputValues);
  const gate = evaluateGate(run.environment, run.samples, run.foreignHeatWorkersDuringRun);

  return {
    condition: run.condition,
    runId: run.id,
    mode: run.mode,
    sampleCount: run.samples.length,
    failureCount: run.failures.length,
    commit,
    total,
    input,
    foreignHeatWorkersDuringRun: run.foreignHeatWorkersDuringRun,
    provenance: run.foreignHeatWorkersDuringRun === null ? 'unknown' : 'known',
    commitHistogram: histogram(commitValues, commitHistogramEdges()),
    overCommitBudget: {
      count: countOver(commitValues, config.ACK_COMMIT_BUDGET_MS),
      fraction: fractionOver(commitValues, config.ACK_COMMIT_BUDGET_MS),
    },
    overNfr1Budget: {
      count: countOver(totalValues, config.TAP_ACK_BUDGET_MS),
      fraction: fractionOver(totalValues, config.TAP_ACK_BUDGET_MS),
    },
    worstCommitMs: commit.max,
    checks: buildChecks(run.condition, commit, total, gate.admissible),
    gate,
    p99Underpowered: run.samples.length < config.HARNESS_P99_MIN_SAMPLES,
  };
}

/**
 * Design finding F-9, determined from the measurements.
 *
 * F-9: LIVE-2 requires the count flash to *begin* inside NFR-1's budget; SES-2 forbids
 * acknowledging an uncommitted mutation. If commits exceed the budget the two collide and the
 * requirements define no behavior. The design's position is to hold SES-2 absolutely and return
 * the collision to the stakeholder — which is a decision, not an implementation workaround, and
 * therefore needs this number to be triggered.
 */
export function determineF9(
  conditions: readonly ConditionReport[],
  nfr1Basis: Nfr1Basis = computeNfr1Basis(conditions),
): F9Determination {
  // Known-clean only. A run that cannot attest its provenance must not be able to publish a
  // `breached` finding — one did, and it escalated a stakeholder decision the clean data did not
  // support. `gate.admissible` already excludes it; the intent is restated so a future gate change
  // cannot quietly readmit it.
  const admissible = conditions.filter((c) => c.gate.admissible && c.provenance === 'known');

  if (admissible.length === 0) {
    return {
      outcome: 'inconclusive',
      headline: 'F-9 undetermined — no admissible on-device run',
      detail:
        'Every run recorded so far fails the ENV-1 gate. A-1 remains unvalidated and F-9 remains conditional. Install the harness to the home screen on the target iPhone and run all three conditions.',
    };
  }

  const missing = (['warm', 'cold', 'thermal'] as RunCondition[]).filter(
    (c) => !admissible.some((r) => r.condition === c && r.mode === 'auto'),
  );
  const breaching = admissible.filter((c) => c.overNfr1Budget.count > 0);

  if (breaching.length === 0) {
    // A clean *synthetic* sweep is not enough to close F-9. Auto runs exclude touch delivery, so
    // their headroom is overstated by however long the finger-to-JavaScript leg takes — measured at
    // 17–28 ms on the target device, which is a quarter of the whole budget. Closing F-9 on the
    // understated path is the same mistake design §5.3 already made once.
    if (missing.length > 0) {
      return {
        outcome: 'inconclusive',
        headline: `A-1 holds so far — still missing: ${missing.join(', ')}`,
        detail: `No acknowledgment exceeded NFR-1's ${config.TAP_ACK_BUDGET_MS} ms budget in the conditions measured, but §16 U-0 names warm, cold and thermally-loaded. Run the missing ones before closing A-1.`,
      };
    }
    if (nfr1Basis.basis !== 'liveTap') {
      return {
        outcome: 'inconclusive',
        headline: 'A-1 holds on synthetic taps — a live-tap run is still needed to close F-9',
        detail:
          `All three conditions are inside NFR-1's ${config.TAP_ACK_BUDGET_MS} ms budget, but every one of them dispatched synthetically and therefore excluded touch delivery. ` +
          'That leg is real and it is not small. Record a live-tap run — the only run anchored on a real pointerup — before treating F-9 as closed.',
      };
    }
    return {
      outcome: 'holds',
      headline: 'A-1 holds under all three conditions, on real taps — F-9 does not fire',
      detail:
        `No acknowledgment exceeded NFR-1's ${config.TAP_ACK_BUDGET_MS} ms budget under warm, cold or thermally-loaded conditions, and the real-touch path stays inside it too. ` +
        `${nfr1Basis.note} ` +
        "SES-2's commit-before-ack rule and LIVE-2's flash budget do not collide, so F-9 stays closed and no stakeholder decision is needed.",
    };
  }

  const worst = breaching.reduce((a, b) =>
    b.overNfr1Budget.fraction > a.overNfr1Budget.fraction ? b : a,
  );
  const pct = (worst.overNfr1Budget.fraction * 100).toFixed(1);

  return {
    outcome: 'breached',
    headline: `F-9 FIRES — ${pct}% of taps under "${worst.condition}" exceeded NFR-1's ${config.TAP_ACK_BUDGET_MS} ms budget`,
    detail:
      `Worst observed acknowledgment: ${Math.round(worst.total.max)} ms; worst commit alone: ${Math.round(worst.worstCommitMs)} ms. ` +
      'SES-2 forbids acknowledging an uncommitted mutation and LIVE-2 requires the flash to begin inside the budget, so these two requirements now collide on real hardware. ' +
      'Per design §5.3 this is a stakeholder decision about what acknowledgment means — not an implementation workaround. Take this report to Andrew before U-9 is built. ' +
      nfr1Basis.note,
  };
}

/**
 * Works out which measurements the NFR-1 statement rests on, and how far the synthetic path
 * understates the real one.
 *
 * Auto runs anchor on the moment before dispatch, so `inputMs` is identically zero and the touch
 * delivery leg — measured at 17–28 ms on a real iPhone — is simply missing from their `totalMs`.
 * Live-tap runs anchor on a real `pointerup` and include it. Whatever the report says about NFR-1
 * headroom is therefore taken from the live-tap path whenever one is admissible.
 */
export function computeNfr1Basis(conditions: readonly ConditionReport[]): Nfr1Basis {
  // Admissible AND known-clean. `admissible` already implies known provenance, because unknown
  // provenance is a blocker — but the intent is stated explicitly here so that a future change to
  // the gate cannot silently readmit unverifiable runs into the comparison.
  const usable = conditions.filter((c) => c.gate.admissible && c.provenance === 'known');
  const live = usable.filter((c) => c.mode === 'liveTap');
  const synthetic = usable.filter((c) => c.mode === 'auto');
  const admissible = usable;

  const worstOf = (rows: readonly ConditionReport[]): number | null =>
    rows.length === 0 ? null : Math.max(...rows.map((r) => r.total.p95));

  const syntheticTotalP95Ms = worstOf(synthetic);

  if (live.length === 0) {
    return {
      basis: admissible.length === 0 ? 'none' : 'synthetic',
      realInputP50Ms: null,
      realTotalP95Ms: null,
      syntheticTotalP95Ms,
      understatementMs: null,
      anomalous: false,
      note:
        admissible.length === 0
          ? 'No admissible run, so nothing can be said about NFR-1 yet.'
          : 'NFR-1 rests on synthetic taps only. Auto runs dispatch from inside JavaScript, so they exclude touch delivery entirely and understate the real tap-to-acknowledgment path. Record a live-tap run before quoting any headroom figure.',
    };
  }

  const realTotalP95Ms = worstOf(live) as number;
  const realInputP50Ms = Math.max(...live.map((c) => c.input.p50));
  const rawUnderstatement =
    syntheticTotalP95Ms === null ? null : realTotalP95Ms - syntheticTotalP95Ms;

  // A real tap carries touch delivery that a synthetic one does not, so the real path cannot be
  // faster. A negative difference is arithmetically impossible for a valid comparison; print the
  // bug signal, never the nonsense figure.
  const anomalous = rawUnderstatement !== null && rawUnderstatement < 0;

  const headline = `NFR-1 rests on real taps: p95 ${Math.round(realTotalP95Ms)} ms, of which ~${Math.round(realInputP50Ms)} ms is touch delivery before any of our code runs.`;

  if (anomalous) {
    return {
      basis: 'liveTap',
      realInputP50Ms,
      realTotalP95Ms,
      syntheticTotalP95Ms,
      understatementMs: null,
      anomalous: true,
      note:
        `${headline} The synthetic comparator (${Math.round(syntheticTotalP95Ms as number)} ms p95) came out SLOWER than the real path, which is impossible — a real tap includes touch delivery and a synthetic one does not. ` +
        'That means the comparator is drawing on a run it should not be. The real figure above stands; the synthetic comparison is suppressed as unreliable.',
    };
  }

  return {
    basis: 'liveTap',
    realInputP50Ms,
    realTotalP95Ms,
    syntheticTotalP95Ms,
    understatementMs: rawUnderstatement,
    anomalous: false,
    note:
      rawUnderstatement === null
        ? headline
        : `NFR-1 rests on real taps: p95 ${Math.round(realTotalP95Ms)} ms against ${Math.round(syntheticTotalP95Ms as number)} ms synthetic — the automatic runs understate the real path by ${Math.round(rawUnderstatement)} ms, because ~${Math.round(realInputP50Ms)} ms of touch delivery happens before any of our code runs. Design §5.3's headroom reasoning was calibrated against the understated figure; use the real one.`,
  };
}

/**
 * A-1: "a two-row IndexedDB `readwrite` on a small database commits within ~40 ms p95 on the target
 * iPhone, warm" (design §0.1) — the single highest-risk unvalidated assumption in the design.
 *
 * Decided only from admissible, known-clean runs. A run that cannot attest its own provenance does
 * not get a vote here, in either direction.
 */
export function determineA1(conditions: readonly ConditionReport[]): A1Determination {
  const usable = conditions.filter((c) => c.gate.admissible && c.provenance === 'known');
  const withA1 = usable.filter((c) => c.checks.some((k) => k.id === 'A-1'));

  if (withA1.length === 0) {
    return {
      outcome: 'unsettled',
      headline: 'A-1 NOT YET SETTLED',
      detail:
        'No admissible, known-clean warm run has been recorded on the target device. A-1 remains the design’s highest-risk unvalidated assumption. Install to the home screen and run the warm condition.',
      runIds: [],
    };
  }

  const failing = withA1.filter((c) => c.checks.some((k) => k.id === 'A-1' && k.verdict === 'fail'));
  const runIds = withA1.map((c) => c.runId);
  const worstP95 = Math.max(...withA1.map((c) => c.commit.p95));

  if (failing.length > 0) {
    return {
      outcome: 'failing',
      headline: `A-1 FAILS — commit p95 ${Math.round(worstP95)} ms against a ${config.A1_ASSUMED_WARM_P95_MS} ms budget`,
      detail:
        'The design assumed a two-row readwrite commits inside this budget on the target device. It does not. Every decision resting on that assumption returns to the stakeholder.',
      runIds: failing.map((c) => c.runId),
    };
  }

  return {
    outcome: 'passing',
    headline: `A-1 SETTLED — PASSING. Commit p95 ${Math.round(worstP95)} ms against a ${config.A1_ASSUMED_WARM_P95_MS} ms budget`,
    detail:
      `The design's highest-risk unvalidated assumption holds on the target device, measured across ${withA1.length} admissible run(s): ${runIds.join(', ')}. ` +
      'SES-2 can hold commit-before-acknowledgment without breaching NFR-1.',
    runIds,
  };
}

export function buildReport(runs: readonly RunRecord[], generatedAt: number): HarnessReport {
  const conditions = runs.map(reportForRun);
  const anyAdmissible = conditions.some((c) => c.gate.admissible);
  const nfr1Basis = computeNfr1Basis(conditions);

  // Named, never silently dropped. A reader must be able to see that a stored run is being ignored
  // and why, rather than wonder whether it vanished.
  const excluded = conditions
    .filter((c) => !c.gate.admissible)
    .map((c) => ({
      runId: c.runId,
      condition: c.condition,
      reason: c.gate.blockers[0] ?? 'inadmissible',
    }));

  return {
    generatedAt,
    conditions,
    gate: {
      admissible: anyAdmissible,
      blockers: anyAdmissible ? [] : [...new Set(conditions.flatMap((c) => c.gate.blockers))],
      warnings: [...new Set(conditions.flatMap((c) => c.gate.warnings))],
    },
    a1: determineA1(conditions),
    nfr1Basis,
    f9: determineF9(conditions, nfr1Basis),
    excluded,
  };
}

/**
 * The one-line answer, for the top of the results screen — legible at arm's length, outdoors.
 * Everything else on the screen supports this sentence.
 */
export function headlineFor(report: ConditionReport): string {
  const a1 = report.checks.find((c) => c.id === 'A-1');
  const nfr1 = report.checks.find((c) => c.id === 'NFR-1');
  const primary = a1 ?? nfr1;
  if (!primary) return 'No verdict available.';
  if (primary.verdict === 'inconclusive') return 'NOT EVIDENCE — see ENV-1 blockers below';
  const label = primary.verdict === 'pass' ? 'WITHIN' : 'OVER';
  return `${label} budget — ${primary.statistic} ${Math.round(primary.observedMs)} ms vs ${primary.budgetMs} ms`;
}
